import {
	useIsMutating,
	useMutation,
	useQuery,
	useQueryClient,
	type QueryClient,
	type UseMutationOptions,
} from '@tanstack/react-query';
import {
	getOnboardingService,
	patchOnboardingService,
	postComunicadoAnalyzeService,
	postOnboardingAnalysisService,
	postOnboardingRouteService,
	type AnalysisResult,
	type ComunicadoAnalysisResult,
	type ComunicadoAnalyzeBody,
	type OnboardingPatch,
	type OnboardingRecord,
	type RouteResult,
} from '../services/onboarding.service';

export const ONBOARDING_QUERY_KEY = ['onboarding'] as const;
const ONBOARDING_UPDATE_MUTATION_KEY = ['onboarding', 'update'] as const;
const ONBOARDING_ANALYSIS_QUERY_KEY = ['onboarding', 'analysis'] as const;
const ONBOARDING_ANALYSIS_REGENERATE_KEY = [
	'onboarding',
	'analysis',
	'regenerate',
] as const;

export const useOnboarding = (options?: { enabled?: boolean }) =>
	useQuery<OnboardingRecord | null>({
		queryKey: ONBOARDING_QUERY_KEY,
		queryFn: getOnboardingService,
		enabled: options?.enabled ?? true,
	});

type OnboardingUpdateContext = { previous: OnboardingRecord | null | undefined };

// Mutation config for the onboarding PATCH, extracted from the hook so its
// optimistic behavior is unit-testable against a bare QueryClient (no React /
// DOM). The hook is a thin `useMutation(createUpdateOnboardingMutationOptions)`.
export const createUpdateOnboardingMutationOptions = (
	queryClient: QueryClient,
): UseMutationOptions<
	OnboardingRecord,
	unknown,
	OnboardingPatch,
	OnboardingUpdateContext
> => ({
	mutationKey: ONBOARDING_UPDATE_MUTATION_KEY,
	mutationFn: patchOnboardingService,
	// Optimistic update: write the patched answers / current_step into the
	// onboarding cache SYNCHRONOUSLY, before any `await`. Call sites (GoalsStep /
	// OtherGoalStep) fire-and-forget this mutation and navigate in the same tick,
	// trusting the next view to read the patched goal/current_step from cache on
	// its first render. `cancelQueries` therefore runs AFTER the write, not
	// before — awaiting it first would defer the write past that synchronous
	// navigation, so the scheduler step saw no goal and bounced back to /goals
	// (the "pick a goal → screen flickers → doesn't advance until a 2nd tap"
	// bug). A manual setQueryData updates the query's revert snapshot, so a
	// cancelled in-flight GET reverts to the optimistic state, never clobbering
	// it.
	onMutate: async (patchBody) => {
		const previous = queryClient.getQueryData<OnboardingRecord | null>(
			ONBOARDING_QUERY_KEY,
		);
		if (previous) {
			const now = new Date().toISOString();
			const mergedAnswers = patchBody.answers
				? {
						...previous.answers,
						...Object.fromEntries(
							Object.entries(patchBody.answers).map(([k, v]) => [
								k,
								{ ...v, answered_at: now },
							]),
						),
					}
				: previous.answers;
			queryClient.setQueryData<OnboardingRecord>(ONBOARDING_QUERY_KEY, {
				...previous,
				current_step:
					patchBody.current_step !== undefined
						? patchBody.current_step
						: previous.current_step,
				completed:
					patchBody.completed !== undefined
						? patchBody.completed
						: previous.completed,
				answers: mergedAnswers,
				updated_at: now,
			});
		}
		await queryClient.cancelQueries({ queryKey: ONBOARDING_QUERY_KEY });
		return { previous };
	},
	onError: (_err, _vars, context) => {
		if (context?.previous !== undefined) {
			queryClient.setQueryData(ONBOARDING_QUERY_KEY, context.previous);
		}
	},
	onSuccess: (updated) => {
		queryClient.setQueryData(ONBOARDING_QUERY_KEY, updated);
	},
});

export const useUpdateOnboarding = () => {
	const queryClient = useQueryClient();
	return useMutation(createUpdateOnboardingMutationOptions(queryClient));
};

// True while ANY useUpdateOnboarding instance has a PATCH in flight. Mutation
// state in React Query is per-hook-instance, so a view that renders a spinner
// against one instance while mutating through another (e.g. a flow hook's
// isPending vs the view's own useUpdateOnboarding) never sees the in-flight
// state — this shared signal is what loading/disabled UI must read.
export const useIsOnboardingSaving = () =>
	useIsMutating({ mutationKey: ONBOARDING_UPDATE_MUTATION_KEY }) > 0;

// useOnboardingAnalysis fetches the AI-generated fiscal analysis. The backend
// is idempotent (caches under answers.ai_analysis) and we use useQuery with
// staleTime: Infinity to fire once per session and reuse the result. retry is
// kept low because a 503 (Claude key missing in dev) or 429 (rate limit) are
// not transient — we want to fail through to the fallback UI fast.
//
// Cache invalidation is deliberate, not automatic: callers use
// `useRegenerateAnalysis` at flow completion or via a manual "regenerate"
// button, so changing an answer mid-flow does not silently spend the user's
// daily generation cap.
//
// Pass `enabled: false` to defer the call (e.g. only fire it when the user
// actually reaches the analysis step).
export const useOnboardingAnalysis = (enabled = true) => {
	const isRegenerating = useIsAnalysisRegenerating();
	return useQuery<AnalysisResult>({
		queryKey: ONBOARDING_ANALYSIS_QUERY_KEY,
		queryFn: () => postOnboardingAnalysisService(),
		// While a regenerate (refresh=true) is in flight we suppress the regular
		// fetch — both targets the same query key and the regenerate's onSuccess
		// will populate the cache. Once the mutation settles, this re-enables;
		// staleTime: Infinity prevents a redundant refetch when data is present.
		enabled: enabled && !isRegenerating,
		staleTime: Infinity,
		retry: 1,
	});
};

// True whenever any `useRegenerateAnalysis` mutation is in flight. Used by
// AnalysisView to keep the loading panel visible while the fresh generation
// is being computed by the backend.
export const useIsAnalysisRegenerating = () =>
	useIsMutating({ mutationKey: ONBOARDING_ANALYSIS_REGENERATE_KEY }) > 0;

// useRegenerateAnalysis forces a fresh analysis generation by hitting the
// backend with `?refresh=true`. The backend bypasses cache lookup but still
// applies the daily rate limit (default 3 fresh generations per user/day),
// so a 429 here means the user has burned through their cap. On success the
// React Query cache is updated in place so any mounted analysis screen
// re-renders with the new content.
export const useRegenerateAnalysis = () => {
	const queryClient = useQueryClient();
	return useMutation<AnalysisResult>({
		mutationKey: ONBOARDING_ANALYSIS_REGENERATE_KEY,
		mutationFn: () => postOnboardingAnalysisService({ refresh: true }),
		onSuccess: (result) => {
			queryClient.setQueryData(ONBOARDING_ANALYSIS_QUERY_KEY, result);
			queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
		},
	});
};

// useClassifyIntent sends the user's free-text goal ("Otra cosa") to the
// backend classifier and resolves to the onboarding flow that best matches it.
// Stateless: it does not touch the onboarding cache — the caller persists the
// resolved goal via useUpdateOnboarding before navigating.
export const useClassifyIntent = () =>
	useMutation<RouteResult, Error, string>({
		mutationFn: (text: string) => postOnboardingRouteService(text),
	});

// useComunicadoAnalyze sends the SAT notice the user uploaded (text and/or
// image/PDF) for AI analysis and resolves to the verdict + actions the flow
// routes on. Stateless: the caller persists what it needs via
// useUpdateOnboarding before navigating.
export const useComunicadoAnalyze = () =>
	useMutation<ComunicadoAnalysisResult, Error, ComunicadoAnalyzeBody>({
		mutationFn: postComunicadoAnalyzeService,
	});
